"use client";
import { useState } from "react";

/** Only what a manual dial actually needs — the lead-detail page and the
 *  leads table hand over differently-shaped lead objects. */
export type CallableLead = { id?: string; name?: string; phone?: string | null; email?: string | null };
import { getCallSid } from "@/app/_module/contexts/VoiceDeviceProvider";
import { requestManualCallPause } from "@/app/_module/contexts/TwilioDeviceContext";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { toast } from "@/app/_module/components/ui/use-toast";
import { useTwilioDevice } from "@/app/_module/hooks/useTwilioDevice";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import api from "@/app/_module/services/api";
import { callService } from "@/app/_module/services/callService";
import { getApiUrl } from "@/app/_module/services/config";

export type CallInitiated = { callId: string; status: string; phoneNumber: string };

/**
 * The ONE manual-dial path for a lead: forwarding vs browser, mic permission,
 * multi-line auto-stop, session record, toasts.
 *
 * `callLead(to?)` dials `to` when given and the lead's own phone otherwise —
 * the call is still attributed to `lead?.id`, so an alternate number off a
 * custom field logs against the same lead.
 */
export function useLeadCall(lead: CallableLead | undefined, onCallInitiated?: (c: CallInitiated) => void) {
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  const { data: phoneNumbers = [] } = useUserPhoneNumbers();
  const { sessionActive, dialerMode, stopDialerSession } = useUnifiedDialer();
  const { isDeviceReady, isDeviceRegistering, connect, forwardCallsToPhone } = useTwilioDevice();

  const validatePhoneNumber = (phone: string): boolean => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10 || cleaned.length === 11;
  };

  const formatPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    if (cleaned.length === 11 && cleaned.startsWith('1')) return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    return phone;
  };

  const callLead = async (to?: string) => {
    const targetPhone = (to || lead?.phone || "").trim();
    if (!targetPhone) {
      toast({
        title: "No Phone Number",
        description: "This lead does not have a phone number.",
        variant: "destructive",
      });
      return;
    }

    if (!validatePhoneNumber(targetPhone)) {
      toast({
        title: "Invalid Phone Number",
        description: "This lead has an invalid phone number format.",
        variant: "destructive",
      });
      return;
    }

    if (phoneNumbers.length === 0) {
      toast({
        title: "No Active Phone Numbers",
        description: "You need at least one active phone number to make calls. Go to Settings > Phone Numbers to purchase one.",
        variant: "destructive",
      });
      return;
    }

    setIsInitiatingCall(true);

    try {
      // Check user preference: Phone forwarding or browser calling
      if (forwardCallsToPhone) {
        // Use phone-to-phone method (call forwarding)
        try {
          // Create session record FIRST for lead details phone forwarding
          const sessionResponse = await api.post({
            url: getApiUrl('/communication/sessions/manual-call'),
            body: {
              toNumber: targetPhone,
              leadId: lead?.id,
            },
            auth: true,
          });
          const result = await callService.initiateCall({
            to: targetPhone,
            from: undefined,
            lead_id: lead?.id,
            recording_enabled: true,
            metadata: {
              lead_name: lead?.name,
              lead_email: lead?.email || "",
              source: "lead_details_forwarding",
            }
          });

          if (result && result.call_id) {
            toast({
              title: "Call Forwarding",
              description: `Your phone will ring to connect to ${lead?.name || "this lead"}`,
            });

            // Notify parent component to show the ActiveCallWidget
            if (onCallInitiated) {
              onCallInitiated({
                callId: result.call_id,
                status: 'initiated',
                phoneNumber: targetPhone,
              });
            }
          }
        } catch (error) {

          toast({
            title: "Call Failed",
            description: "Failed to initiate call forwarding. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsInitiatingCall(false);
        }
        return;
      }

      // Use browser calling (modern method)
      if (!isDeviceReady) {
        toast({
          title: "Device Not Ready",
          description: isDeviceRegistering ? "Initializing calling device..." : "Calling device is not ready. Please refresh the page or enable call forwarding in Settings.",
          variant: "destructive",
        });
        setIsInitiatingCall(false);
        return;
      }

      // Request microphone permission before starting call
      const { requestMicrophoneWithGuidance } = await import("@/app/_module/utils/microphone-permission");
      const permissionResult = await requestMicrophoneWithGuidance();

      if (!permissionResult.granted) {
        toast({
          title: "Microphone Access Required",
          description: permissionResult.error || "Please allow microphone access to make calls.",
          variant: "destructive",
        });
        setIsInitiatingCall(false);
        return;
      }

      // Create session record FIRST to ensure database integrity
      const sessionResponse = await api.post({
        url: getApiUrl('/communication/sessions/manual-call'),
        body: {
          toNumber: targetPhone,
          leadId: lead?.id,
        },
        auth: true,
      });
      // AUTO-STOP: If multi-line dialer is running, end the session before making the manual call.
      // Must stop BOTH frontend (state + localStorage) AND backend (session status → completed).
      // Without backend stop, handleRaceAnswerCallEnd keeps creating batches from lead_queue.
      if (sessionActive && dialerMode === 'multiline') {
        requestManualCallPause(); // sessionStorage flag for recovery skip
        stopDialerSession(); // Clear frontend session state
        // Clear persisted session so it won't auto-recover on page reload
        try { localStorage.removeItem('insuracentral_dialer_session'); } catch {}
        // End ALL active multi-line sessions for this user on the backend (fire-and-forget)
        try {
          await api.post({
            url: getApiUrl('/communication/multi-line-conference/end-all-active'),
            body: { reason: 'manual_call_from_lead_detail' },
            auth: true,
          });
        } catch {
          // Non-critical — session will auto-complete when conference dies
        }
      }

      // Use Twilio Voice SDK browser calling
      // Backend smart area code matching selects the best caller ID per lead
      const call = await connect({
        to: targetPhone,
        leadId: lead?.id,
        metadata: {
          lead_name: lead?.name,
          lead_email: lead?.email || "",
          source: "lead_details",
        }
      });

      if (call) {
        toast({
          title: "Call Connected",
          description: `Calling ${lead?.name || "this lead"} at ${formatPhoneNumber(targetPhone)}`,
        });

        // Notify parent component to show the ActiveCallWidget
        if (onCallInitiated) {
          onCallInitiated({
            callId: getCallSid(call) ?? '',
            status: 'initiated',
            phoneNumber: targetPhone,
          });
        }
      }
    } catch (error) {

      let errorMessage = "Failed to initiate call. Please try again.";
      if (error && typeof error === 'object' && 'message' in error) {
        const message = (error as { message: string }).message;
        if (message.includes('session')) {
          errorMessage = "Failed to create call session. Please try again.";
        } else {
          errorMessage = message;
        }
      }

      toast({
        title: "Call Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsInitiatingCall(false);
    }
  };

  return { callLead, isInitiatingCall };
}
